/**
 * Site-wide critical reminder banner (owner design 2026-08-25):
 * "like a customer sev1 / downtime in progress — all hands on deck."
 *
 * Mounted in the app SHELL, so it renders on every screen. Shows when a
 * CRITICAL contract's latest delivered firing has no acknowledgement —
 * "Ok" is firing-scoped: it hides the banner until the NEXT firing, never
 * longer. Snooze hides until the snooze expires; Complete and Cancel end
 * it everywhere at once (they act on the server contract).
 *
 * A pinned banner, not a blocking modal: a sev1 runs for hours and a
 * modal on every navigation would train the user to hate it. This one
 * simply will not leave until acted on.
 */
import { useState } from 'react'
import { AlarmClock, Check, ChevronDown, Clock3, X } from 'lucide-react'
import {
  isBannerCritical,
  reminderAction,
  type ReminderContract,
} from '../../services/reminderService'
import { useActiveReminders } from './useActiveReminders'

const SNOOZES = [
  { key: '10m', label: '10 min' },
  { key: '1h', label: '1 hour' },
  { key: 'this_evening', label: 'This evening' },
]

export function CriticalReminderBanner() {
  const [busy, setBusy] = useState(false)
  const [snoozeOpen, setSnoozeOpen] = useState<number | null>(null)
  // 2026-08-29: was its own fetch + 45s interval; now the shared store, so
  // the obligation strip and this banner read one answer on one clock. The
  // predicate moved to isBannerCritical() — the strip excludes exactly what
  // this renders, instead of both drawing the same firing contract.
  const { contracts, refresh: load } = useActiveReminders()
  const critical = contracts.filter(isBannerCritical)

  if (critical.length === 0) return null

  const run = async (
    c: ReminderContract,
    action: 'done' | 'snooze' | 'acknowledge' | 'stop',
    target?: string,
  ) => {
    setBusy(true)
    try {
      await reminderAction(c.id, action, target,
        action === 'acknowledge' && c.latest_firing_id != null
          ? { firing_id: c.latest_firing_id }
          : undefined)
      setSnoozeOpen(null)
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="alert"
      className="sticky top-0 z-[60] border-b-2 border-bad/60 bg-tint-bad-soft shadow-[0_4px_16px_rgba(183,90,74,0.25)]"
    >
      {critical.map((c) => (
        <div
          key={c.id}
          className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-2.5"
        >
          <AlarmClock className="h-4 w-4 shrink-0 text-bad animate-pulse" />
          <span className="text-app-micro font-bold uppercase tracking-widest text-bad">
            critical
          </span>
          <p
            className="min-w-0 flex-1 truncate text-sm font-bold text-primary"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {c.obligation_text}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(c, 'done')}
              className="min-h-9 rounded-lg bg-ok px-3 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Check className="h-3.5 w-3.5" /> Complete
            </button>
            <div className="relative">
              <button
                type="button"
                disabled={busy}
                onClick={() => setSnoozeOpen(snoozeOpen === c.id ? null : c.id)}
                className="min-h-9 rounded-lg border border-bad/40 bg-surface px-3 text-xs text-secondary hover:bg-surface-alt disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Clock3 className="h-3.5 w-3.5" /> Snooze <ChevronDown className="h-3 w-3" />
              </button>
              {snoozeOpen === c.id && (
                <div className="absolute right-0 z-[70] mt-1 w-36 rounded-xl border border-soft bg-surface p-1 shadow-lg">
                  {SNOOZES.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => void run(c, 'snooze', o.key)}
                      className="block w-full rounded-lg px-3 py-2 text-left text-xs text-primary hover:bg-surface-alt"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              disabled={busy}
              title="Ok — I know. Quiet until the next reminder."
              onClick={() => void run(c, 'acknowledge')}
              className="min-h-9 rounded-lg border border-soft bg-surface px-3 text-xs font-bold text-secondary hover:bg-surface-alt disabled:opacity-50"
            >
              Ok
            </button>
            <button
              type="button"
              disabled={busy}
              title="Cancel this reminder entirely"
              onClick={() => void run(c, 'stop')}
              className="min-h-9 rounded-lg px-2 text-xs text-muted hover:text-bad hover:bg-bad/10 disabled:opacity-50 inline-flex items-center"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
